/* Presentation only: never writes product prices, variant IDs, or cart state. */
if (!customElements.get('ipg-savings-badge')) {
  customElements.define('ipg-savings-badge', class extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._root = this.closest('.product__item-js');
      this._onSelection = (event) => {
        const picker = event.target instanceof Element && event.target.closest('variant-radios-detail, variant-group-detail, variant-radios, variant-radios-sticky, variant-group-sticky, variant-radios-single');
        if (!picker || picker.closest('.product__item-js') !== this._root) return;
        queueMicrotask(() => {
          if (this.isConnected && 'currentVariant' in picker) this.updateVariant(picker.currentVariant);
        });
      };
      this._root?.addEventListener('change', this._onSelection);
      this._root?.addEventListener('click', this._onSelection);
      if (this.dataset.placement === 'percent' || this.dataset.placement === 'detail') {
        this._row = this.dataset.placement === 'detail' ? this.closest('.ipg-price-controls .card-product-price') : this.closest('.ipg-card-price');
        if (this._row) {
          this._resize = new ResizeObserver(() => this.scheduleFit());
          this._resize.observe(this._row.parentElement);
          this._mutations = new MutationObserver(() => this.scheduleFit());
          this._mutations.observe(this._row, { childList: true, subtree: true, characterData: true });
          document.fonts?.ready.then(() => this.scheduleFit());
        }
      } else if (this.dataset.placement === 'card') {
        this._resize = new ResizeObserver(() => this.scheduleFit());
        this._resize.observe(this.parentElement);
      }
      if (this._row || this.dataset.placement === 'card') {
        this._breakpoint = matchMedia('(min-width: 768px)');
        this._onBreakpoint = () => this.scheduleFit();
        this._breakpoint.addEventListener('change', this._onBreakpoint);
      }
      queueMicrotask(() => {
        if (!this.isConnected) return;
        const picker = this._root?.querySelector('variant-radios-detail, variant-group-detail, variant-radios, variant-radios-single');
        if (picker && 'currentVariant' in picker) this.updateVariant(picker.currentVariant);
        this.scheduleFit();
        this.updateClearance();
      });
    }
    disconnectedCallback() {
      this._root?.removeEventListener('change', this._onSelection);
      this._root?.removeEventListener('click', this._onSelection);
      this._resize?.disconnect();
      this._mutations?.disconnect();
      this._breakpoint?.removeEventListener('change', this._onBreakpoint);
      cancelAnimationFrame(this._fitFrame);
      this._connected = false;
    }
    updateVariant(variant) {
      const price = variant && Number(variant.price);
      const compare = variant && Number(variant.compare_at_price);
      const valid = variant && Number.isSafeInteger(price) && Number.isSafeInteger(compare) && price >= 0 && compare > price && compare > 0;
      this.hidden = !valid;
      if (valid) {
        const savings = compare - price;
        const format = this.dataset.moneyFormat || window.themeGlobalVariables?.settings?.money_format;
        const formatter = window.Shopify?.formatMoney;
        if (typeof formatter !== 'function' || !format) {
          // Keep a correct server-rendered value only when the variant is unchanged.
          if (String(variant.id) !== this.dataset.variantId || price !== Number(this.dataset.price) || compare !== Number(this.dataset.compare)) this.hidden = true;
          return;
        }
        const formatted = formatter(savings, format);
        const money = new DOMParser().parseFromString(String(formatted), 'text/html').body.textContent.trim();
        const prefix = this.dataset.prefix || '';
        const percent = Math.round(savings * 100 / compare);
        const text = this.dataset.placement === 'percent' ? (percent > 0 ? `-${percent}%` : '<1%') : `${prefix} ${money}`;
        const textNode = this.querySelector('.ipg-savings-badge__text') || this;
        if (textNode.textContent !== text) textNode.textContent = text;
        const label = `${prefix} ${money}`;
        if (this.getAttribute('aria-label') !== label) this.setAttribute('aria-label', label);
        this.dataset.variantId = String(variant.id);
        this.dataset.price = String(price);
        this.dataset.compare = String(compare);
      }
      this.scheduleFit();
      this.updateClearance();
    }
    updateClearance() {
      if (this.dataset.placement !== 'card' || this.hidden) return;
      const media = this.closest('.product-item__inner');
      if (media) media.style.setProperty('--ipg-card-save-clearance', `${this.offsetTop + this.offsetHeight}px`);
    }
    scheduleFit() {
      if ((!this._row && this.dataset.placement !== 'card') || !this.isConnected || this._fitFrame) return;
      this._fitFrame = requestAnimationFrame(() => {
        this._fitFrame = null;
        if (this._row) this.fitPriceRow();
        else this.fitCardBadge();
      });
    }
    fitCardBadge() {
      if (this.hidden || !this.parentElement.clientWidth) return;
      this.removeAttribute('data-ipg-fit-save');
      this.style.setProperty('--ipg-save-fit', '1');
      const css = getComputedStyle(this);
      this.style.setProperty('--ipg-base-save-font', css.fontSize);
      const padding = (parseFloat(css.paddingLeft) || 0) + (parseFloat(css.paddingRight) || 0);
      const available = this.parentElement.clientWidth - 2 * (parseFloat(css.left) || 0) - padding;
      const range = document.createRange();
      range.selectNodeContents(this);
      const textWidth = range.getBoundingClientRect().width;
      this.setAttribute('data-ipg-fit-save', '');
      if (textWidth > available) this.style.setProperty('--ipg-save-fit', String(Math.max(0.05, (available - 1) / textWidth)));
      this.updateClearance();
    }
    fitPriceRow() {
      const row = this._row;
      const rowCss = getComputedStyle(row);
      const available = row.clientWidth - (parseFloat(rowCss.paddingLeft) || 0) - (parseFloat(rowCss.paddingRight) || 0);
      if (!available || !row.getClientRects().length) return;
      row.style.setProperty('--ipg-price-fit', '1');
      const nodes = row.querySelectorAll('.price, .compare-price, .unit-price, ipg-savings-badge');
      nodes.forEach(node => node.removeAttribute('data-ipg-fit-font'));
      const fonts = [...nodes].map(node => [node, getComputedStyle(node).fontSize]);
      fonts.forEach(([node, font]) => {
        node.style.setProperty('--ipg-base-font', font);
        node.setAttribute('data-ipg-fit-font', '');
      });
      const width = () => {
        const children = [...row.children].filter(e => getComputedStyle(e).display !== 'none');
        return children.reduce((sum, e) => {
          const css = getComputedStyle(e);
          return sum + e.getBoundingClientRect().width + (parseFloat(css.marginLeft) || 0) + (parseFloat(css.marginRight) || 0);
        }, 0) + Math.max(0, children.length - 1) * (parseFloat(getComputedStyle(row).columnGap) || 0);
      };
      const children = [...row.children].filter(e => getComputedStyle(e).display !== 'none');
      const fixed = children.reduce((sum, e) => {
        const css = getComputedStyle(e);
        return sum + ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth', 'marginLeft', 'marginRight'].reduce((n, key) => n + (parseFloat(css[key]) || 0), 0);
      }, 0) + Math.max(0, children.length - 1) * (parseFloat(getComputedStyle(row).columnGap) || 0);
      const natural = width();
      let ratio = natural > available ? Math.max(0.05, (available - fixed - 1) / Math.max(1, natural - fixed)) : 1;
      row.style.setProperty('--ipg-price-fit', String(ratio));
      for (let i = 0; i < 4 && width() > available - 0.5; i++) {
        ratio *= (available - 1) / width();
        row.style.setProperty('--ipg-price-fit', String(Math.max(0.05, ratio)));
      }
    }
  });
}
